import Nango, { AuthError } from "@nangohq/frontend";
import { NOTIFICATION_TYPES } from "./index";

/**
 * Integration authentication types
 */
export const AUTH_TYPES = {
  OAUTH: "oauth", // OAuth via Nango
  API_KEY: "api_key", // API Key authentication
  BASIC: "basic", // Basic auth (username/password)
  BEARER_TOKEN: "bearer_token", // Bearer token
};

/**
 * Nango error types from documentation
 * @see https://nango.dev/docs/reference/sdks/frontend
 */
export const NANGO_ERROR_TYPES = {
  MISSING_AUTH_TOKEN: "missing_auth_token",
  BLOCKED_BY_BROWSER: "blocked_by_browser",
  INVALID_HOST_URL: "invalid_host_url",
  MISSING_CREDENTIALS: "missing_credentials",
  WINDOW_CLOSED: "window_closed",
  CONNECTION_TEST_FAILED: "connection_test_failed",
  MISSING_CONNECT_SESSION_TOKEN: "missing_connect_session_token",
  RESOURCE_CAPPED: "resource_capped",
  UNKNOWN_ERROR: "unknown_error",
};

/**
 * Get user-friendly error message for Nango errors
 * @param {AuthError} error - Nango AuthError instance
 * @param {string} integrationId - Integration identifier
 * @returns {string} User-friendly error message
 */
const getNangoErrorMessage = (error, integrationId) => {
  const integrationName =
    integrationId.charAt(0).toUpperCase() + integrationId.slice(1);

  const errorMessages = {
    [NANGO_ERROR_TYPES.MISSING_AUTH_TOKEN]:
      "Authentication configuration error. Please refresh the page and try again.",
    [NANGO_ERROR_TYPES.BLOCKED_BY_BROWSER]:
      "Your browser blocked the authentication popup. Please allow popups for this site and try again.",
    [NANGO_ERROR_TYPES.INVALID_HOST_URL]:
      "Invalid authentication server URL. Please contact support.",
    [NANGO_ERROR_TYPES.MISSING_CREDENTIALS]: `Please provide all required credentials to connect ${integrationName}.`,
    [NANGO_ERROR_TYPES.WINDOW_CLOSED]:
      "Authentication window was closed before completing. Please try again.",
    [NANGO_ERROR_TYPES.CONNECTION_TEST_FAILED]: `Failed to verify ${integrationName} credentials. Please check your permissions and try again.`,
    [NANGO_ERROR_TYPES.MISSING_CONNECT_SESSION_TOKEN]:
      "Session expired. Please refresh the page and try again.",
    [NANGO_ERROR_TYPES.RESOURCE_CAPPED]:
      "Integration limit reached. Please contact support to increase your limit.",
    [NANGO_ERROR_TYPES.UNKNOWN_ERROR]: `An unexpected error occurred while connecting ${integrationName}. Please try again.`,
  };

  return (
    errorMessages[error.type] ||
    error.message ||
    `Failed to connect ${integrationName}. Please try again.`
  );
};

/**
 * Generic integration connection configuration
 * @typedef {Object} IntegrationConfig
 * @property {string} integrationId - Integration identifier (e.g., "hubspot", "amplitude")
 * @property {string} authType - Authentication type from AUTH_TYPES
 * @property {Object} authConfig - Authentication-specific configuration
 * @property {Function} apiClient - Axios instance for API calls
 * @property {number} teamId - Team ID
 * @property {number} userId - User ID
 * @property {string} userEmail - User email
 * @property {Function} showToast - Toast notification function
 * @property {Function} getOrCreateNangoSessionToken - Function to get Nango session token (for OAuth)
 * @property {string} teamName - Team name (for OAuth)
 * @property {Function} onSuccess - Optional callback on successful connection
 * @property {Function} onError - Optional callback on error
 * @property {string} endpoint - API endpoint for saving integration (default: "integration/team-integrations/")
 */

/**
 * Performs OAuth authentication via Nango
 * @param {Object} config - OAuth configuration
 * @returns {Promise<Object>} Integration data
 * @throws {Error} With user-friendly message for Nango errors
 */
const authenticateOAuth = async ({
  integrationId,
  teamId,
  userId,
  userEmail,
  teamName,
  getOrCreateNangoSessionToken,
  authConfig = {},
}) => {
  try {
    // Get Nango session token
    const nangoConnectSessionToken = await getOrCreateNangoSessionToken(
      userId,
      userEmail,
    );

    // Initialize Nango client
    const nango = new Nango({
      connectSessionToken: nangoConnectSessionToken,
      ...authConfig.nangoOptions,
    });

    // Perform OAuth authentication
    const nangoTeamId = `${teamName}-${teamId}`;

    // Support OAuth with credentials (for Basic Auth integrations like Amplitude)
    // If credentials are provided in authConfig, pass them to nango.auth()
    const nangoAuthOptions = authConfig.credentials
      ? {
          credentials: authConfig.credentials,
          ...(authConfig.params && { params: authConfig.params }),
        }
      : undefined;

    const authResult = await nango.auth(integrationId, nangoAuthOptions);

    const integrationData = {
      name: `${userId}:${nangoTeamId}`,
      connection_id: authResult.connectionId,
      integration_id: integrationId,
      team: teamId,
      auth_type: AUTH_TYPES.OAUTH,
    };

    if (authConfig.config) {
      integrationData.config = authConfig.config;
    }

    return integrationData;
  } catch (error) {
    // Handle Nango-specific errors
    if (error instanceof AuthError) {
      const userMessage = getNangoErrorMessage(error, integrationId);
      const enhancedError = new Error(userMessage);
      enhancedError.originalError = error;
      enhancedError.errorType = error.type;
      enhancedError.isNangoError = true;
      throw enhancedError;
    }

    // Re-throw other errors
    throw error;
  }
};

/**
 * Performs API Key authentication
 * @param {Object} config - API Key configuration
 * @returns {Promise<Object>} Integration data
 * @throws {Error} If API key is missing or invalid
 */
const authenticateApiKey = async ({
  integrationId,
  teamId,
  userId,
  authConfig,
}) => {
  const {
    apiKey,
    apiKeyName = "api_key",
    ...additionalCredentials
  } = authConfig;

  if (!apiKey || typeof apiKey !== "string" || apiKey.trim() === "") {
    const integrationName =
      integrationId.charAt(0).toUpperCase() + integrationId.slice(1);
    throw new Error(
      `API Key is required to connect ${integrationName}. Please provide a valid API key.`,
    );
  }

  // Build credentials object with API key and any additional fields
  const credentials = {
    [apiKeyName]: apiKey.trim(),
    ...additionalCredentials,
  };

  return {
    name: `${userId}:${integrationId}`,
    integration_id: integrationId,
    team: teamId,
    auth_type: AUTH_TYPES.API_KEY,
    credentials,
  };
};

/**
 * Performs Basic authentication
 * @param {Object} config - Basic auth configuration
 * @returns {Promise<Object>} Integration data
 * @throws {Error} If username or password is missing or invalid
 */
const authenticateBasic = async ({
  integrationId,
  teamId,
  userId,
  authConfig,
}) => {
  const { username, password } = authConfig;
  const integrationName =
    integrationId.charAt(0).toUpperCase() + integrationId.slice(1);

  if (!username || typeof username !== "string" || username.trim() === "") {
    throw new Error(
      `Username is required to connect ${integrationName}. Please provide a valid username.`,
    );
  }

  if (!password || typeof password !== "string" || password.trim() === "") {
    throw new Error(
      `Password is required to connect ${integrationName}. Please provide a valid password.`,
    );
  }

  return {
    name: `${userId}:${integrationId}`,
    integration_id: integrationId,
    team: teamId,
    auth_type: AUTH_TYPES.BASIC,
    credentials: {
      username: username.trim(),
      password,
    },
  };
};

/**
 * Performs Bearer Token authentication
 * @param {Object} config - Bearer token configuration
 * @returns {Promise<Object>} Integration data
 * @throws {Error} If bearer token is missing or invalid
 */
const authenticateBearerToken = async ({
  integrationId,
  teamId,
  userId,
  authConfig,
}) => {
  const { token } = authConfig;
  const integrationName =
    integrationId.charAt(0).toUpperCase() + integrationId.slice(1);

  if (!token || typeof token !== "string" || token.trim() === "") {
    throw new Error(
      `Bearer token is required to connect ${integrationName}. Please provide a valid token.`,
    );
  }

  return {
    name: `${userId}:${integrationId}`,
    integration_id: integrationId,
    team: teamId,
    auth_type: AUTH_TYPES.BEARER_TOKEN,
    credentials: {
      token: token.trim(),
    },
  };
};

/**
 * Authentication strategy map
 */
const authStrategies = {
  [AUTH_TYPES.OAUTH]: authenticateOAuth,
  [AUTH_TYPES.API_KEY]: authenticateApiKey,
  [AUTH_TYPES.BASIC]: authenticateBasic,
  [AUTH_TYPES.BEARER_TOKEN]: authenticateBearerToken,
};

/**
 * Sends integration data to backend
 * @param {Object} apiClient - Axios instance
 * @param {string} endpoint - API endpoint
 * @param {Object} integration - Integration data
 * @returns {Promise<Object>} API response
 */
const sendIntegrationToBackend = async (apiClient, endpoint, integration) => {
  try {
    const response = await apiClient.post(endpoint, integration);
    return response;
  } catch (err) {
    console.error("Failed to send integration to backend:", err);
    const errorMsg =
      err.response?.data?.message ||
      err.response?.data?.error ||
      "Failed to create integration. Please try again.";
    throw new Error(errorMsg);
  }
};

/**
 * Generic integration connection handler
 * @param {IntegrationConfig} config - Integration configuration
 * @returns {Promise<Object>} Connection result with integration data
 */
export const connectIntegration = async (config) => {
  const {
    integrationId,
    authType,
    authConfig = {},
    apiClient,
    teamId,
    userId,
    userEmail,
    showToast,
    getOrCreateNangoSessionToken,
    teamName,
    onSuccess,
    onError,
    endpoint = "integration/team-integrations/",
  } = config;

  try {
    // Validate required fields
    if (!integrationId) {
      throw new Error("Integration ID is required");
    }
    if (!authType) {
      throw new Error("Authentication type is required");
    }
    if (!apiClient) {
      throw new Error("API client is required");
    }
    if (!teamId || !userId) {
      throw new Error("Team ID and User ID are required");
    }

    // Get authentication strategy
    const authStrategy = authStrategies[authType];
    if (!authStrategy) {
      throw new Error(`Unsupported authentication type: ${authType}`);
    }

    // OAuth-specific validation
    if (authType === AUTH_TYPES.OAUTH) {
      if (!getOrCreateNangoSessionToken) {
        throw new Error(
          "Nango session token function is required for OAuth authentication",
        );
      }
      if (!teamName) {
        throw new Error("Team name is required for OAuth authentication");
      }
    }

    // Perform authentication
    const integrationData = await authStrategy({
      integrationId,
      teamId,
      userId,
      userEmail,
      teamName,
      getOrCreateNangoSessionToken,
      authConfig,
    });

    // Send integration to backend
    const response = await sendIntegrationToBackend(
      apiClient,
      endpoint,
      integrationData,
    );

    // Show success toast
    if (showToast) {
      showToast({
        content: `${integrationId.charAt(0).toUpperCase() + integrationId.slice(1)} connected successfully!`,
        type: NOTIFICATION_TYPES.success,
      });
    }

    // Call success callback
    if (onSuccess) {
      await onSuccess(response.data);
    }

    return {
      success: true,
      integration: response.data,
    };
  } catch (err) {
    // Categorize and log error with context
    const errorContext = {
      integrationId,
      authType,
      errorType: err.isNangoError
        ? "nango_error"
        : err.response
          ? "backend_error"
          : "validation_error",
      nangoErrorType: err.errorType || null,
    };

    console.error(`Failed to connect ${integrationId}:`, err, errorContext);

    // Determine user-friendly error message
    let errorMsg;
    if (err.isNangoError) {
      // Nango errors already have user-friendly messages
      errorMsg = err.message;
    } else if (err.response?.data) {
      // Backend errors
      errorMsg =
        err.response.data.message ||
        err.response.data.error ||
        err.response.data.detail ||
        `Failed to connect ${integrationId}. Please check your credentials and try again.`;
    } else if (err.message) {
      // Validation or other errors
      errorMsg = err.message;
    } else {
      // Fallback
      errorMsg = `Failed to connect ${integrationId}. Please try again.`;
    }

    // Show error toast with categorized message
    if (showToast) {
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    }

    // Call error callback with enhanced error object
    if (onError) {
      const enhancedError = {
        ...err,
        message: errorMsg,
        context: errorContext,
      };
      onError(enhancedError);
    }

    throw err;
  }
};

/**
 * Generic integration disconnection handler
 * @param {Object} config - Disconnection configuration
 * @returns {Promise<void>}
 */
export const disconnectIntegration = async ({
  integrationId,
  integration,
  apiClient,
  showToast,
  onSuccess,
  onError,
  endpoint = "integration/team-integrations/",
}) => {
  try {
    if (!integration?.id) {
      throw new Error("Integration ID is required for disconnection");
    }

    const response = await apiClient.delete(`${endpoint}${integration.id}/`);

    if (response.status !== 204) {
      throw new Error(`Failed to disconnect ${integrationId}`);
    }

    // Show success toast
    if (showToast) {
      showToast({
        content: `${integrationId.charAt(0).toUpperCase() + integrationId.slice(1)} disconnected successfully!`,
        type: NOTIFICATION_TYPES.success,
      });
    }

    // Call success callback
    if (onSuccess) {
      await onSuccess();
    }

    return {
      success: true,
    };
  } catch (err) {
    console.error(`Failed to disconnect ${integrationId}:`, err);
    const errorMsg =
      err.response?.data?.error ||
      `Failed to disconnect ${integrationId}. Please try again.`;

    // Show error toast
    if (showToast) {
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    }

    // Call error callback
    if (onError) {
      onError(err);
    }

    throw err;
  }
};

/**
 * Helper to get team name (used for OAuth integrations)
 * @param {Object} apiClient - Axios instance
 * @returns {Promise<string>} Team name
 */
export const getTeamName = async (apiClient) => {
  try {
    const response = await apiClient.get("/teams/");
    return response.data.results[0].name;
  } catch (err) {
    console.error("Failed to fetch team information:", err);
    throw new Error("Failed to fetch team information. Please try again.");
  }
};
